import { CakeTopologyBidirectionalDirection, CakeTopologyStateSnapshot, EfCakeTopologyUiState } from 'src/app/shared/models/ef-cake-topology.model';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';

export abstract class EfCakeTopologyStateUtils {
  
  static isNodeActivated(
    nodeId: string,
    activeEdges: Set<string>,
    bidirectionalEdgeDirections: Record<string, CakeTopologyBidirectionalDirection>): boolean {
    if (nodeId === EFateConstants.DEFAULT_NODE_NAMES.Parent) {
      return true;
    }

    const hasSingleDirectionActivation = Object.entries(EFateConstants.EDGE_TO_TARGET_NODE).some(([edgeId, targetNode]) => {
      return targetNode === nodeId && activeEdges.has(edgeId);
    });

    if (hasSingleDirectionActivation) {
      return true;
    }

    return Object.entries(EFateConstants.BIDIRECTIONAL_EDGE_NODES).some(([edgeId, nodes]) => {
      const direction = bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
      if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD) {
        return nodes.to === nodeId;
      }

      if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE) {
        return nodes.from === nodeId;
      }

      return false;
    });
  }

  static buildUiState(parsed: Partial<EfCakeTopologyUiState> | null | undefined): EfCakeTopologyUiState | undefined {
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }

    const groups = Array.isArray(parsed.groups) && parsed.groups.length > 0
      ? parsed.groups.map((group, index) => ({
          groupKey: group?.groupKey || `${EFateConstants.GROUP_KEY_PREFIX}${index + 1}`,
          groupName: group?.groupName || `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} ${index + 1}`,
          topologyKey: group?.topologyKey || EFateConstants.DEFAULT_TOPOLOGY_KEY,
        }))
      : [{
          groupKey: EFateConstants.DEFAULT_GROUP_KEY,
          groupName: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1`,
          topologyKey: EFateConstants.DEFAULT_TOPOLOGY_KEY,
        }];

    const rawTopologyByGroup = parsed.topologyByGroup ?? {};
    const topologyByGroup = groups.reduce((acc, group) => {
      const snapshot = rawTopologyByGroup[group.groupKey] as Partial<CakeTopologyStateSnapshot> | undefined;
      acc[group.groupKey] = {
        activeEdges: [...(snapshot?.activeEdges ?? [])],
        bidirectionalEdges: { ...(snapshot?.bidirectionalEdges ?? {}) },
        activeStatusNodes: [...(snapshot?.activeStatusNodes ?? EFateConstants.DEFAULT_ACTIVE_STATUS_NODES)],
      };
      return acc;
    }, {} as Record<string, CakeTopologyStateSnapshot>);

    const parsedSelectedGroupKey = typeof parsed.selectedGroupKey === 'string' ? parsed.selectedGroupKey : undefined;
    const selectedGroupKey = groups.some(group => group.groupKey === parsedSelectedGroupKey)
      ? (parsedSelectedGroupKey as string)
      : groups[0].groupKey;

    return {
      selectedGroupKey,
      nodeNames: { ...EFateConstants.DEFAULT_NODE_NAMES, ...(parsed.nodeNames ?? {}) },
      groups,
      topologyByGroup,
    };
  }
}
